import { OFFICE_ST_AUTH_DIALOG_PATH } from './officeStAuthConfig';

export type OfficeStAuthDialogResult =
    | { status: 'success'; accessToken: string; refreshToken?: string }
    | { status: 'error'; code?: string | number; message?: string };

function getDialogUrl(): string {
    const origin =
        typeof window !== 'undefined' ? window.location.origin.replace(/\/+$/, '') : '';
    return `${origin}${OFFICE_ST_AUTH_DIALOG_PATH}`;
}

export function openOfficeStAuthDialog(): Promise<{ accessToken: string; refreshToken?: string }> {
    return new Promise((resolve, reject) => {
        const officeUi = (Office as any)?.context?.ui;
        if (!officeUi || typeof officeUi.displayDialogAsync !== 'function') {
            reject(new Error('Office dialog API is not available'));
            return;
        }

        officeUi.displayDialogAsync(
            getDialogUrl(),
            { height: 60, width: 30, displayInIframe: false },
            (asyncResult: any) => {
                if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
                    reject(
                        new Error(
                            asyncResult.error?.message ||
                                'Failed to open ST SSO sign-in dialog'
                        )
                    );
                    return;
                }

                const dialog = asyncResult.value;

                const cleanup = () => {
                    try {
                        dialog.close();
                    } catch {
                        // dialog may already be closed
                    }
                };

                dialog.addEventHandler(
                    (Office as any).EventType.DialogMessageReceived,
                    (arg: { message?: string; error?: number }) => {
                        if (arg.error) {
                            cleanup();
                            reject(new Error(`Dialog error: ${arg.error}`));
                            return;
                        }

                        try {
                            const msg = JSON.parse(arg.message || '{}') as OfficeStAuthDialogResult;
                            cleanup();
                            if (msg.status === 'success' && msg.accessToken) {
                                resolve({
                                    accessToken: msg.accessToken,
                                    refreshToken: msg.refreshToken,
                                });
                                return;
                            }
                            const errorMessage =
                                msg.status === 'error'
                                    ? msg.message || 'ST SSO sign-in did not return a session token'
                                    : 'ST SSO sign-in did not return a session token';
                            reject(new Error(errorMessage));
                        } catch (parseError) {
                            cleanup();
                            reject(parseError);
                        }
                    }
                );

                dialog.addEventHandler(
                    (Office as any).EventType.DialogEventReceived,
                    (arg: { error?: number; message?: string }) => {
                        cleanup();
                        reject(
                            new Error(
                                arg.message ||
                                    `Sign-in dialog closed (error ${arg.error ?? 'unknown'})`
                            )
                        );
                    }
                );
            }
        );
    });
}
